import {
  IconoirCheck,
  IconoirXmark,
  IconoirNavArrowUp,
  IconoirEye,
} from "@ey-xd/motif-icon";

import {
  MotifButton,
  MotifErrorMessage,
  MotifIcon,
  MotifProgressLoader,
  MotifTooltip,
  MotifTruncate,
} from "@ey-xd/motif-react";
import CustomPagination from "../../components/CustomPagination";
import { useNavigate } from "react-router";
import ApproveSkuModal from "../../components/ApproveSkuModal";
import RejectSkuModal from "../../components/RejectSkuModal/RejectSkuModal";
import { useEffect, useId, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import skuListEmptyImg from "../../assets/images/empty_bmo_sku_list.svg";
import SearchInput from "../../components/SearchInput/SearchInput";
import { AppDispatch, RootState } from "../../store";
import { fetchBMOApprovalListData } from "../../store/slices/CTDBBMOApprovalListSlice";
import { processSkuApproval } from "../../store/slices/CTDBBMOApprovalSlice";
import "../BMOHome/BMOHome.scss";
import { BMO_DESCRIPTION, BMO_LABEL } from "../CTProductCostSetup/Constants";
import { containsEmoji, formatUSDWithCommas } from "../../utils/common";
import { EMOJI_ERROR_MESSAGE } from "../../Constant";
import { isEmpty } from "../../utils/Validator";
import { convertDateStringtoDate, dateFunc } from "../../utils/dateFunction";
import { approveNewCost } from "../../store/slices/CTDBBMOCostUpdateSlice";

const BMOHome = () => {
  const dispatch = useDispatch<AppDispatch>();
  const uniqueID = useId();
  const navigate = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [paginationObj, setPaginationObj] = useState<any>({
    currentPage: 1,
    pageSize: 10,
    searchText: "",
  });

  const [showRejectSkuModal, setShowRejectSkuModal] = useState<boolean>(false);
  const [showApproveSkuModal, setShowApproveSkuModal] =
    useState<boolean>(false);
  const [selectedSku, setSelectedSku] = useState<string>("");
  const [selectedSkuName, setSelectedSkuName] = useState<string | null>(null);
  const [searchValidationError, setSearchValidationError] =
    useState<string>("");
  const [fileBackendError, setFileBackendError] = useState(false);
  const isLoading = useSelector(
    (state: RootState) => state.CTDBBMOApprovalListSlice.isLoading,
  );
  const data = useSelector(
    (state: RootState) => state.CTDBBMOApprovalListSlice.data,
  );
  const error = useSelector(
    (state: RootState) => state.CTDBBMOApprovalListSlice.error,
  );

  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const [isSKUApproveLoading, setIsSKUApproveLoading] = useState(false);
  const [skuApproveErrorMessage, setSkuApproveErrorMessage] =
    useState<string>("");
  const [rejectReasonApiError, setRejectReasonApiError] = useState<string>("");
  useEffect(() => {
    dispatch(
      fetchBMOApprovalListData({
        Search: paginationObj.searchText,
        Page: paginationObj.currentPage,
        Record: paginationObj.pageSize,
      }),
    );
  }, [dispatch, paginationObj]);

  useEffect(() => {
    window.history.scrollRestoration = "manual";
  }, []);

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const handleSearch = (searchText: string) => {
    setPaginationObj({
      ...paginationObj,
      currentPage: 1,
      pageSize: Number(paginationObj.pageSize),
      searchText: searchText,
    });
  };

  const getCostInterval = (costInterval: string) => {
    switch (costInterval) {
      case "month":
        return "Per month";
      case "onetime":
        return "One time";
      case "week":
        return "Per week";
      case "autotransact":
        return "Auto Transaction";
      default:
        return "NA";
    }
  };

  const handleApproveClick = (skuId: string, skuName: string) => {
    setSkuApproveErrorMessage("");
    setSelectedSku(skuId);
    setSelectedSkuName(skuName);
    setShowApproveSkuModal(true);
  };

  const getSelectedSku = (skuId: string | null) => {
    if (data === null || skuId === null) {
      return null;
    }
    return data.Result.find((sku) => sku.SkuId === skuId);
  };

  const bmoSkuRejectHandler = async (
    action?: string,
    msg?: string,
    skuId?: string,
  ) => {
    try {
      setRejectReasonApiError("");
      setIsLoadingAction(true);
      const skuRejectObj = {
        Action: action,
        SkuId: skuId || selectedSku,
        ...{ Message: msg },
      };
      const response = await dispatch(processSkuApproval(skuRejectObj));
      if (response.payload.Status === "success") {
        setRejectReasonApiError("");
        setShowRejectSkuModal(false);
        setIsLoadingAction(false);
        let pageNo = paginationObj.currentPage;
        if (pageNo != 1 && data?.Result?.length == 1) {
          pageNo = paginationObj.currentPage - 1;
        }
        setPaginationObj({
          ...paginationObj,
          currentPage: pageNo,
        });
      } else {
        setIsLoadingAction(false);
        setRejectReasonApiError(
          response?.payload?.message ||
            "Failed to reject SKU. Please try again.",
        );
      }
    } catch (error) {
      console.log("Error in rejecting sku", error);
      setIsLoadingAction(false);
      setRejectReasonApiError("Failed to reject SKU. Please try again.");
    }
  };

  const bmoSkuApproveHandler = async (action?: string, file?: File) => {
    try {
      setSkuApproveErrorMessage("");
      setIsSKUApproveLoading(true);
      const selectedSKUData = getSelectedSku(selectedSku);
      const skuApproveObj = {
        Action: action || "",
        SkuId: selectedSku,
        UpdateCost: selectedSKUData?.UpdateCost || null,
        UpdateNSR: selectedSKUData?.UpdateNSR || null,
        UpdateIndexation: selectedSKUData?.UpdateIndexation || null,
        UpdatePublishdtm: selectedSKUData?.UpdatePublishDtm || "",
        file,
        ...(getRequestType(selectedSKUData) === "Cost Update"
          ? { UpdateCostId: selectedSKUData?.UpdateCostId }
          : {}),
      };
      const response = await dispatch(approveNewCost(skuApproveObj));

      if (response.payload.Status === "success") {
        setShowApproveSkuModal(false);
        setIsSKUApproveLoading(false);
        setSkuApproveErrorMessage("");
        let pageNo = paginationObj.currentPage;
        if (pageNo != 1 && data?.Result?.length == 1) {
          pageNo = paginationObj.currentPage - 1;
        }
        setPaginationObj({
          ...paginationObj,
          currentPage: pageNo,
        });
      } else if (response?.payload?.message === "Invalid file") {
        setIsSKUApproveLoading(false);
        setFileBackendError(true);
      } else {
        setSkuApproveErrorMessage(
          response?.payload?.status === 400 && response?.payload?.message
            ? response.payload.message
            : "Something went wrong while approving SKU. Please try again.",
        );
        setIsSKUApproveLoading(false);
      }
    } catch (error) {
      console.log("Error in approving sku", error);
      setSkuApproveErrorMessage(
        "Something went wrong while approving SKU. Please try again.",
      );
      setIsSKUApproveLoading(false);
    }
  };

  const handleRejectClick = (
    skuId: string,
    skuName: string,
    publishDate: string,
  ) => {
    setRejectReasonApiError("");
    if (isDatePassed(publishDate)) {
      bmoSkuRejectHandler(
        "skurejectcost",
        "The SKU could not be approved because the cost published date has passed.",
        skuId,
      );
    } else {
      setSelectedSku(skuId);
      setSelectedSkuName(skuName);
      setShowRejectSkuModal(true);
    }
  };

  const handleEditClick = (productId: string, skuId: string) => {
    setSelectedSku(skuId);
    navigate(`/ct/bmo/product/${productId}/sku-id/${skuId}/sku-approval`);
  };

  const getRequestType = (sku: any) => {
    if (isEmpty(sku)) return "";
    if (
      sku.StatusCd === "sku_active" &&
      sku.UpdateStatusCd === "skuupdate_submitted"
    ) {
      return "Cost Update";
    } else {
      return "New SKU";
    }
  };

  const getRoundedAmount = (amount: string) => {
    const amountInNumber = Number(amount);
    if (Number.isInteger(amountInNumber)) {
      return amountInNumber.toFixed(2);
    }
    return amount;
  };

  const getRequestCost = (sku: any) => {
    if (isEmpty(sku)) return "";
    if (
      sku.StatusCd === "sku_active" &&
      sku.UpdateStatusCd === "skuupdate_submitted"
    ) {
      return getRoundedAmount(sku.UpdateCost);
    } else {
      return getRoundedAmount(sku.CostMetricCost);
    }
  };

  const getRequestDate = (sku: any) => {
    if (isEmpty(sku)) return "";
    return dateFunc(sku.UpdatedDtm).formattedDateOnly;
  };

  const getRequestUser = (sku: any) => {
    if (isEmpty(sku)) return "";
    if (
      sku.StatusCd === "sku_active" &&
      sku.UpdateStatusCd === "skuupdate_submitted"
    ) {
      return sku.UpdatedUser;
    } else {
      return sku.CreatedUser;
    }
  };

  const isDatePassed = (publishDate: string) => {
    if (!publishDate || publishDate.trim() === "") return false;
    const minDateConverted = convertDateStringtoDate(publishDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return minDateConverted !== null && minDateConverted < today;
  };

  return (
    <div className="bmo-home">
      <div className="main-content-header">
        <div className="bmo-header">
          <h1 className="header-title">{BMO_LABEL}</h1>
          <div className="bmo-header-content">{BMO_DESCRIPTION}</div>
        </div>
      </div>
      <div className="sku-list">
        {isLoading && (
          <MotifProgressLoader
            aria-busy="true"
            aria-label="This page is loading"
            aria-live="polite"
            arialabel="This page is loading"
            show
            variant="default"
            className="bmo-progress"
          />
        )}

        {!isLoading && !data && error && (
          <div className="bmo-empty-list">
            <div>
              <img src={skuListEmptyImg} alt="Empty SKU List" />
            </div>
            <div className="empty-skulist-header">
              An error has occurred while attempting to retrieve the data.
              Please choose the option to refresh the page.
            </div>
            <div className="empty-skulist-button-wrapper">
              <MotifButton
                className="empty-skulist-button"
                onClick={() => window.location.reload()}
              >
                Refresh
              </MotifButton>
            </div>
          </div>
        )}

        {!isLoading &&
          paginationObj.searchText == "" &&
          !error &&
          data &&
          data.Result &&
          data.Result.length === 0 && (
            <div className="bmo-empty-list">
              <div>
                <img src={skuListEmptyImg} alt="Empty SKU List" />
              </div>
              <div className="empty-skulist-header">
                No BMO Pending Approvals
              </div>
            </div>
          )}
        {!isLoading &&
          paginationObj.searchText != "" &&
          !error &&
          data &&
          data.Result &&
          data.Result.length === 0 && (
            <>
              <div className="sku-list-header">
                <div>
                  <h2>BMO Pending Approvals </h2>
                </div>
                <div className="sku-list-header-btns">
                  <SearchInput
                    id={`sku-search-product-details- ${uniqueID}`}
                    aria-label={"Search"}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      handleSearch(e.target.value);
                    }}
                    clearSearch={() => {
                      setPaginationObj({
                        ...paginationObj,
                        searchText: "",
                        currentPage: 1,
                        pageSize: 10,
                      });
                    }}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onEnter={(e: any) => handleSearch(e.target.value)}
                    placeholder={"Search"}
                    value={paginationObj.searchText}
                    className="sku-search-input"
                  />
                </div>
              </div>
              <table className="motif-table-standard">
                <thead>
                  <tr>
                    <th className="th-skuName-bmo">SKU Name/ SKU#</th>
                    <th className="th-reqested-date">Requested Date</th>
                    <th className="th-reqested-type">Request Type</th>
                    <th className="th-createdBy-bmo">Requested By</th>
                    <th className="th-billingType-bmo">Billing Type</th>
                    <th className="th-costInterval-bmo">Cost Interval</th>
                    <th className="th-costBasis-bmo">Cost Basis</th>
                    <th className="th-status-bmo">Status</th>
                    <th className="th-action-bmo">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={7} className="no-data">
                      No BMO reviews matching the search criteria.
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

        {!isLoading &&
          !error &&
          data &&
          data.Result &&
          data.Result.length > 0 && (
            <>
              <div className="sku-list-header">
                <div>
                  <h2>BMO Pending Approvals</h2>
                </div>
                <div className="sku-list-header-btns">
                  <SearchInput
                    id={`sku-search-product-details- ${uniqueID}`}
                    aria-label={"Search"}
                    onChange={(e) => {
                      const isContainEmoji = containsEmoji(e.target.value);
                      if (isContainEmoji) {
                        setSearchValidationError(EMOJI_ERROR_MESSAGE);
                      } else {
                        setSearchValidationError("");
                      }
                    }}
                    clearSearch={() => {
                      setSearchValidationError("");
                      setPaginationObj({
                        ...paginationObj,
                        searchText: "",
                        currentPage: 1,
                        pageSize: 10,
                      });
                    }}
                    onEnter={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      const isContainEmoji = containsEmoji(
                        e.currentTarget.value,
                      );
                      if (!isContainEmoji) {
                        handleSearch(e.currentTarget.value);
                      }
                    }}
                    placeholder={"Search"}
                    value={paginationObj.searchText}
                    className="sku-search-input"
                  />
                  {searchValidationError && (
                    <MotifErrorMessage className="search-helper-text">
                      {searchValidationError}
                    </MotifErrorMessage>
                  )}
                </div>
              </div>
              <table className="motif-table-standard">
                <thead>
                  <tr>
                    <th className="th-skuName-bmo">SKU Name/ SKU#</th>
                    <th className="th-reqested-date">Requested Date</th>
                    <th className="th-reqested-type">Request Type</th>
                    <th className="th-createdBy-bmo">Requested By</th>
                    <th className="th-billingType-bmo">Billing Type</th>
                    <th className="th-costInterval-bmo">Cost Interval</th>
                    <th className="th-costBasis-bmo">Cost Basis</th>
                    <th className="th-status-bmo">Status</th>
                    <th className="th-action-bmo">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.Result.length > 0 &&
                    data.Result.map((sku: any) => {
                      if (sku.SkuRelation.length === 0) {
                        return (
                          <tr key={sku.SkuId}>
                            <td className="sku-cell">
                              <div className="sku-name-responsive font-regular">
                                <MotifTruncate
                                  maxLines={1}
                                  className="sku-truncate-responsive"
                                >
                                  {sku.Name}
                                </MotifTruncate>
                              </div>
                              <div className="light-clr">{sku.SkuId}</div>
                            </td>
                            <td>{getRequestDate(sku)}</td>
                            <td>{getRequestType(sku)}</td>
                            <td>{getRequestUser(sku)}</td>
                            <td>{sku.BillingTypeCd}</td>
                            <td>{getCostInterval(sku.CostIntervalCd)}</td>
                            <td>
                              $USD {formatUSDWithCommas(getRequestCost(sku))}
                            </td>
                            <td>
                              <div className="table-cell">
                                <div className={`status-grid ${sku.StatusCd}`}>
                                  <span
                                    className={`circle ${sku.StatusCd}`}
                                  ></span>
                                  <span className={`status ${sku.StatusCd}`}>
                                    {sku.StatusName}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="action-btns">
                                <MotifTooltip
                                  allowHover
                                  hideCloseButton={true}
                                  allowClick={false}
                                  contentClassName="bmo-action-btn tooltip-content"
                                  flip
                                  forceHideCounter={0}
                                  id="tooltip"
                                  offset={[0, 8]}
                                  placement="top"
                                  trigger={
                                    <MotifButton
                                      className="sku-edit-btn"
                                      onClick={() =>
                                        handleEditClick(
                                          sku.ProductId,
                                          sku.SkuId,
                                        )
                                      }
                                    >
                                      <MotifIcon
                                        iconFunction={IconoirEye}
                                        size="20"
                                      />
                                    </MotifButton>
                                  }
                                  usePortal
                                  variant="default"
                                >
                                  View
                                </MotifTooltip>

                                <MotifTooltip
                                  allowHover
                                  hideCloseButton={true}
                                  allowClick={false}
                                  contentClassName="bmo-action-btn tooltip-content"
                                  flip
                                  forceHideCounter={0}
                                  id="tooltip"
                                  offset={[0, 8]}
                                  placement="top"
                                  trigger={
                                    <MotifButton
                                      className="sku-approve-btn"
                                      onClick={() =>
                                        handleApproveClick(sku.SkuId, sku.Name)
                                      }
                                      disabled={
                                        isDatePassed(sku.UpdatePublishDtm) &&
                                        getRequestType(sku) === "Cost Update"
                                      }
                                    >
                                      <MotifIcon
                                        iconFunction={IconoirCheck}
                                        size="16"
                                      />
                                    </MotifButton>
                                  }
                                  usePortal
                                  variant="default"
                                >
                                  Approve
                                </MotifTooltip>

                                <MotifTooltip
                                  allowHover
                                  hideCloseButton={true}
                                  allowClick={false}
                                  contentClassName="bmo-action-btn tooltip-content"
                                  flip
                                  forceHideCounter={0}
                                  id="tooltip"
                                  offset={[0, 8]}
                                  placement="top"
                                  trigger={
                                    <MotifButton
                                      className="sku-reject-btn"
                                      onClick={() =>
                                        handleRejectClick(
                                          sku.SkuId,
                                          sku.Name,
                                          sku.UpdatePublishDtm,
                                        )
                                      }
                                    >
                                      <MotifIcon
                                        iconFunction={IconoirXmark}
                                        size="16"
                                      />
                                    </MotifButton>
                                  }
                                  usePortal
                                  variant="default"
                                >
                                  Reject
                                </MotifTooltip>
                              </div>
                            </td>
                          </tr>
                        );
                      } else {
                        return (
                          <tr key={sku.SkuId}>
                            <td>
                              <ul className="tree">
                                <li className="tree-trigger">
                                  <MotifIcon
                                    iconFunction={IconoirNavArrowUp}
                                    size="18"
                                    className="Carret"
                                  />
                                  <div className="table-cell">
                                    <div className="font-regular">
                                      <MotifTruncate
                                        maxLines={1}
                                        maxWidth={200}
                                      >
                                        {sku.Name}
                                      </MotifTruncate>
                                    </div>
                                    <div className="light-clr">{sku.SkuId}</div>
                                  </div>
                                </li>
                                <ul>
                                  {sku.SkuRelation.map((skuRelation: any) => {
                                    return (
                                      <li key={skuRelation.SkuId}>
                                        <div className="table-cell">
                                          <div className="font-regular">
                                            <MotifTruncate
                                              maxLines={1}
                                              maxWidth={200}
                                            >
                                              {skuRelation.Name}
                                            </MotifTruncate>
                                          </div>
                                          <div>{skuRelation.SkuId}</div>
                                        </div>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </ul>
                            </td>
                            <td>
                              <div className="table-cell">
                                {getRequestDate(sku)}
                              </div>
                              {sku.SkuRelation.map((skuRelation: any) => {
                                return (
                                  <div
                                    className="table-cell"
                                    key={skuRelation.ChildSkuId}
                                  >
                                    {getRequestDate(skuRelation)}
                                  </div>
                                );
                              })}
                            </td>
                            <td>
                              <div className="table-cell">
                                {getRequestType(sku)}
                              </div>
                              {sku.SkuRelation.map((skuRelation: any) => {
                                return (
                                  <div
                                    className="table-cell"
                                    key={skuRelation.ChildSkuId}
                                  >
                                    {getRequestType(skuRelation)}
                                  </div>
                                );
                              })}
                            </td>
                            <td>
                              <div className="table-cell">
                                {getRequestUser(sku)}
                              </div>
                              {sku.SkuRelation.map((skuRelation: any) => {
                                return (
                                  <div
                                    className="table-cell"
                                    key={skuRelation.ChildSkuId}
                                  >
                                    {getRequestUser(skuRelation)}
                                  </div>
                                );
                              })}
                            </td>
                            <td>
                              <div className="table-cell">
                                {sku.BillingTypeCd}
                              </div>
                              {sku.SkuRelation.map((skuRelation: any) => {
                                return (
                                  <div
                                    className="table-cell"
                                    key={skuRelation.ChildSkuId}
                                  >
                                    {skuRelation.BillingTypeCd}
                                  </div>
                                );
                              })}
                            </td>
                            <td>
                              <div className="table-cell">
                                {getCostInterval(sku.CostIntervalCd)}
                              </div>
                              {sku.SkuRelation.map((skuRelation: any) => {
                                return (
                                  <div
                                    className="table-cell"
                                    key={skuRelation.ChildSkuId}
                                  >
                                    {getCostInterval(
                                      skuRelation.CostIntervalCd,
                                    )}
                                  </div>
                                );
                              })}
                            </td>
                            <td>
                              <div className="table-cell">
                                $USD {formatUSDWithCommas(sku.CostMetricCost)}
                              </div>
                              {sku.SkuRelation.map((skuRelation: any) => {
                                return (
                                  <div
                                    className="table-cell"
                                    key={skuRelation.ChildSkuId}
                                  >
                                    $USD{" "}
                                    {formatUSDWithCommas(
                                      getRequestCost(skuRelation),
                                    )}
                                  </div>
                                );
                              })}
                            </td>
                            <td>
                              <div className="table-cell">
                                <div className={`status-grid ${sku.StatusCd}`}>
                                  <span
                                    className={`circle ${sku.StatusCd}`}
                                  ></span>
                                  <span className={`status ${sku.StatusCd}`}>
                                    {sku.StatusName}
                                  </span>
                                </div>
                              </div>

                              {sku.SkuRelation.map((skuRelation: any) => {
                                return (
                                  <div
                                    className="table-cell"
                                    key={skuRelation.ChildSkuId}
                                  >
                                    <div
                                      className={`status-grid ${skuRelation.StatusCd}`}
                                    >
                                      <span
                                        className={`circle ${skuRelation.StatusCd}`}
                                      ></span>
                                      <span
                                        className={`status ${skuRelation.StatusCd}`}
                                      >
                                        {skuRelation.StatusName}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </td>
                            <td>
                              <div className="table-cell">
                                <div className="action-btns">
                                  <MotifTooltip
                                    allowHover
                                    hideCloseButton={true}
                                    allowClick={false}
                                    contentClassName="bmo-action-btn tooltip-content"
                                    flip
                                    forceHideCounter={0}
                                    id="tooltip"
                                    offset={[0, 8]}
                                    placement="top"
                                    trigger={
                                      <MotifButton
                                        className="sku-edit-btn"
                                        onClick={() =>
                                          handleEditClick(
                                            sku.ProductId,
                                            sku.SkuId,
                                          )
                                        }
                                      >
                                        <MotifIcon
                                          iconFunction={IconoirEye}
                                          size="20"
                                        />
                                      </MotifButton>
                                    }
                                    usePortal
                                    variant="default"
                                  >
                                    View
                                  </MotifTooltip>

                                  <MotifTooltip
                                    allowHover
                                    hideCloseButton={true}
                                    allowClick={false}
                                    contentClassName="bmo-action-btn tooltip-content"
                                    flip
                                    forceHideCounter={0}
                                    id="tooltip"
                                    offset={[0, 8]}
                                    placement="top"
                                    trigger={
                                      <MotifButton
                                        className="sku-approve-btn"
                                        disabled={
                                          sku.SkuRelation.filter(
                                            (relation: any) =>
                                              relation.StatusCd !==
                                              "sku_active",
                                          ).length > 0
                                        }
                                        onClick={() =>
                                          handleApproveClick(
                                            sku.SkuId,
                                            sku.Name,
                                          )
                                        }
                                      >
                                        <MotifIcon
                                          iconFunction={IconoirCheck}
                                          size="16"
                                        />
                                      </MotifButton>
                                    }
                                    usePortal
                                    variant="default"
                                  >
                                    Approve
                                  </MotifTooltip>

                                  <MotifTooltip
                                    allowHover
                                    hideCloseButton={true}
                                    allowClick={false}
                                    contentClassName="bmo-action-btn tooltip-content"
                                    flip
                                    forceHideCounter={0}
                                    id="tooltip"
                                    offset={[0, 8]}
                                    placement="top"
                                    trigger={
                                      <MotifButton
                                        className="sku-reject-btn"
                                        onClick={() =>
                                          handleRejectClick(
                                            sku.SkuId,
                                            sku.Name,
                                            sku.UpdatePublishDtm,
                                          )
                                        }
                                      >
                                        <MotifIcon
                                          iconFunction={IconoirXmark}
                                          size="16"
                                        />
                                      </MotifButton>
                                    }
                                    usePortal
                                    variant="default"
                                  >
                                    Reject
                                  </MotifTooltip>
                                </div>
                              </div>
                              {sku.SkuRelation.map((skuRelation: any) => {
                                return (
                                  <div
                                    className="table-cell"
                                    key={skuRelation.ChildSkuId}
                                  >
                                    <div className="no-data"></div>
                                  </div>
                                );
                              })}
                            </td>
                          </tr>
                        );
                      }
                    })}

                  {data.Result.length == 0 &&
                    paginationObj.searchText !== "" && (
                      <tr>
                        <td colSpan={7} className="no-data">
                          No BMO reviews matching the search criteria.
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
              <CustomPagination
                currentpage={paginationObj.currentPage}
                pageSize={paginationObj.pageSize}
                disableNext={false}
                onChangePageSize={(page: number) => {
                  setPaginationObj({
                    ...paginationObj,
                    pageSize: Number(page),
                    currentPage: 1,
                  });
                }}
                onChangeNextPage={() => {
                  setPaginationObj({
                    ...paginationObj,
                    currentPage: paginationObj.currentPage + 1,
                  });
                }}
                onChangeFirstPage={() => {
                  setPaginationObj({
                    ...paginationObj,
                    currentPage: 1,
                  });
                }}
                onChangePreviousPage={() => {
                  setPaginationObj({
                    ...paginationObj,
                    currentPage: paginationObj.currentPage - 1,
                  });
                }}
                onChangeLastPage={() => {
                  setPaginationObj({
                    ...paginationObj,
                    currentPage: data?.TotalPages || 0,
                  });
                }}
                pageSizeSelector={[10, 20, 50]}
                totalRecords={data?.TotalRecord || 0}
              />
            </>
          )}
      </div>
      <ApproveSkuModal
        show={showApproveSkuModal}
        setShow={setShowApproveSkuModal}
        selectedSku={selectedSku || ""}
        selectedSkuName={selectedSkuName || ""}
        btnLabel="Approve SKU"
        approveHandler={bmoSkuApproveHandler}
        requestType={getRequestType(getSelectedSku(selectedSku))}
        isLoading={isSKUApproveLoading}
        apiErrorMessage={skuApproveErrorMessage}
        onClearApiError={() => setSkuApproveErrorMessage("")}
        setFileBackendError={setFileBackendError}
        fileBackendError={fileBackendError}
      />
      <RejectSkuModal
        show={showRejectSkuModal}
        setShow={setShowRejectSkuModal}
        rejectHandler={bmoSkuRejectHandler}
        selectedSku={selectedSku || ""}
        selectedSkuName={selectedSkuName || ""}
        requestType={getRequestType(getSelectedSku(selectedSku))}
        isLoading={isLoadingAction}
        apiErrorMessage={rejectReasonApiError}
        onClearApiError={() => setRejectReasonApiError("")}
      />
    </div>
  );
};

export default BMOHome;
